export interface AnalysisDefinition {
  key: string;
  title: string;
  cardDescription: string;
  routeOptions: string[];
  requiresPreparation?: boolean;
  preparationOnly?: boolean;
  guidanceNotes?: string[];
}

export const SAMPLE_PREPARATION_OPTIONS = [
  "Polishing Hardmetall",
  "Fine polishing Hardmetall",
  "Polish for EBSD",
  "Fine polishing OPU for Coating",
  "Metallographic steel sample",
  "Metallographic Ni-alloy",
  "Metallographic Ti-alloy",
  "Metallographic Al-alloy",
  "Sample preparation CBN",
  "Cutting for Fracture cross-section",
  "Other preparation",
];

export const ANALYSIS_DEFINITIONS: AnalysisDefinition[] = [
  {
    key: "carbon",
    title: "Carbon analysis",
    cardDescription: "C",
    routeOptions: ["C"],
  },
  {
    key: "oxygenNitrogen",
    title: "Oxygen / Nitrogen",
    cardDescription: "O, N",
    routeOptions: ["O", "N"],
  },
  {
    key: "hardness",
    title: "Hardness testing",
    cardDescription: "Hv, HRC, K1C, Hardening depth",
    routeOptions: ["Hv10*", "Hv30*", "Hv3*", "HRc**", "K1C", "Hardening depth"],
    requiresPreparation: true,
    guidanceNotes: [
      "*Accredited Vickers hardness testing according to ISO 6507-1.",
      "**According to SS-EN ISO 18265:2013. Test material must be specified.",
    ],
  },
  {
    key: "xrf",
    title: "XRF analysis",
    cardDescription: "HM-program (Co%), Other",
    routeOptions: ["HM-program (Co%)", "Other"],
  },
  {
    key: "icp",
    title: "ICP analysis",
    cardDescription: "ICP analysis",
    routeOptions: ["ICP analysis"],
  },
  {
    key: "xrd",
    title: "XRD analysis",
    cardDescription: "XRD-Analysis, Texture, Residual stress measurement, Other",
    routeOptions: ["XRD", "Texture", "Residual stress", "Other"],
  },
  {
    key: "sem",
    title: "SEM",
    cardDescription: "SEM-photo, Fracture cross-section, EDS-Analysis, Other analyses",
    routeOptions: ["SEM-photo", "Fractography", "EDS", "Other"],
    requiresPreparation: true,
  },
  {
    key: "hcMmDensity",
    title: "Hc / MM / Density",
    cardDescription: "Hc, MM, Density",
    routeOptions: ["Hc", "MM", "Density"],
  },
  {
    key: "mechanicalPreparation",
    title: "Mechanical preparation",
    cardDescription: "Polishing Hardmetall, Fine polishing Hardmetall, Polish for EBSD and more",
    routeOptions: [],
    requiresPreparation: true,
    preparationOnly: true,
  },
  {
    key: "lom",
    title: "LOM",
    cardDescription: "Porosity, Gradient, Corrosion, Etching and more",
    routeOptions: ["Porosity", "Gradient", "Corrosion", "Etching", "Other"],
    requiresPreparation: true,
  },
  {
    key: "other",
    title: "Other analysis",
    cardDescription: "Specify in notes",
    routeOptions: ["Specify in notes"],
  },
];

const LEGACY_ANALYSIS_DEFINITIONS: AnalysisDefinition[] = [
  { key: "oxygen", title: "Oxygen analysis", cardDescription: "Oxygen content verification.", routeOptions: ["Oxygen"] },
  { key: "nitrogen", title: "Nitrogen analysis", cardDescription: "Nitrogen content verification.", routeOptions: ["Nitrogen"] },
  { key: "density", title: "Density", cardDescription: "Bulk and tap density evaluation.", routeOptions: ["Density"] },
  { key: "metallography", title: "Metallography", cardDescription: "Sample prep and structural analysis workflows.", routeOptions: ["Metallography"], requiresPreparation: true },
  { key: "flow", title: "Flow test", cardDescription: "Flowability and process suitability.", routeOptions: ["Flow test"] },
  { key: "magnetic", title: "Magnetic scan", cardDescription: "Magnetic contamination screening.", routeOptions: ["Magnetic scan"] },
  { key: "texture", title: "Texture", cardDescription: "Texture and grain evaluation.", routeOptions: ["Texture"] },
];

const ANALYSIS_DEFINITION_MAP = new Map<string, AnalysisDefinition>(
  [...ANALYSIS_DEFINITIONS, ...LEGACY_ANALYSIS_DEFINITIONS].map(definition => [definition.key, definition]),
);

export function getAllAnalysisDefinitions(): AnalysisDefinition[] {
  return [...ANALYSIS_DEFINITIONS, ...LEGACY_ANALYSIS_DEFINITIONS];
}

export function getAnalysisDefinition(analysisKey: string) {
  return ANALYSIS_DEFINITION_MAP.get(analysisKey);
}

const CURRENT_ANALYSIS_KEYS = new Set(ANALYSIS_DEFINITIONS.map(definition => definition.key));

/** True for the analyses that can be selected on a current order; legacy keys are excluded. */
export function isCurrentAnalysisKey(analysisKey: string): boolean {
  return CURRENT_ANALYSIS_KEYS.has(analysisKey);
}

export function definitionNeedsPreparation(definition: Pick<AnalysisDefinition, "requiresPreparation" | "preparationOnly"> | undefined): boolean {
  return Boolean(definition?.requiresPreparation || definition?.preparationOnly);
}
